"use client";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Card, CircularProgress } from "@mui/material";
import { Formik } from "formik";
import React from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import SWR_KEYS from "@/constants/swr-keys";
import useAuth from "@/contexts/auth-context";
import useGetNotifications from "@/hooks/requests/notification/use-get-notification";
import useMarkManyAsReadNotifications from "@/hooks/requests/notification/use-mark-all-as-read-notifications";
import useFilters from "@/hooks/use-filters";
import useMutate from "@/hooks/use-mutate";
import usePagination from "@/hooks/use-pagination";
import INotification from "@/interfaces/notification.interface";
import { NotificationsMessages } from "@/messages/notifications/notifications.messages";
import { IFilter } from "@/types/filter.types";

import NotificationFiltersForm from "./components/FiltersForm";
import { NotificationTable } from "./components/Table";
import { initialFilterValues } from "./constants";

type Props = {
  messages: NotificationsMessages;
};

export default function NotificationsPage({ messages }: Props) {
  const { user: currentUser } = useAuth();
  const pagination = usePagination();
  const { pageNumber, pageSize } = pagination;
  const filter = useFilters<INotification>({ initialValue: initialFilterValues });
  const filters = filter.filters;
  const markManyAsReadNotifications = useMarkManyAsReadNotifications();
  const { markAllAsRead, isLoading: isLoadingMarkingManyAsRead } = markManyAsReadNotifications;
  const getNotifications = useGetNotifications({ filters, pageNumber, pageSize });
  const { data, error, isLoading: isLoadingNotifications } = getNotifications;
  const { mutate } = useMutate();

  const shouldLoadNotificationsTable = !!data && data.items.length > 0 && !isLoadingNotifications;
  const shouldLoadError = !data && !!error && !isLoadingNotifications;
  const shouldLoadNoNotificationsFound =
    !!data && data.items.length === 0 && !error && !isLoadingNotifications;

  const allNotificationsRead = data?.items.every((notification) => notification.read) ?? false;

  const handleChangeFilters = (newFilters: IFilter<INotification>[]) => {
    filter.changeFilters(newFilters);
  };

  const onSuccess = () => {
    mutate(SWR_KEYS.getNotifications);
  };

  const handleMarkAllAsRead = async () => {
    if (currentUser) {
      const formData = { customerId: currentUser.id };
      const successMessage = messages.markAllNotificationsAsReadSuccess;
      const errorMessage = messages.markAllNotificationsAsReadError;
      await markAllAsRead({ formData, successMessage, errorMessage, onSuccess });
    }
  };

  const isLoading = isLoadingNotifications || isLoadingMarkingManyAsRead;

  return (
    <PageLayout messages={messages} title={messages.notificationsPageTitle}>
      <PageContainer>
        <Formik initialValues={filters} validateOnChange={false} onSubmit={handleChangeFilters}>
          <NotificationFiltersForm messages={messages} />
        </Formik>

        <Card sx={({ functions: { pxToRem } }) => ({ marginTop: pxToRem(16) })}>
          <Suspense isSuspended={isLoading} fallback={<CircularProgress size={50} />}>
            <MDBox p={2}>
              <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6">{messages.notificationsPageTitle}</MDTypography>
                <MDButton
                  variant="gradient"
                  color="dark"
                  isLoading={isLoading}
                  endIcon={<CheckCircleOutlineIcon />}
                  onClick={handleMarkAllAsRead}
                  disabled={allNotificationsRead}
                >
                  {messages.markAllAsReadButton}
                </MDButton>
              </MDBox>

              {shouldLoadNotificationsTable && (
                <NotificationTable pagination={pagination} data={data} messages={messages} />
              )}

              {shouldLoadError && (
                <AlertBlock
                  title={messages.notificationErrorToLoad}
                  description={error.message}
                  alt={messages.notificationNotFoundAlt}
                />
              )}

              {shouldLoadNoNotificationsFound && (
                <AlertBlock
                  title={messages.notificationNotFoundError}
                  description={messages.notificationNotFoundDescription}
                  alt={messages.notificationNotFoundAlt}
                />
              )}
            </MDBox>
          </Suspense>
        </Card>
      </PageContainer>
    </PageLayout>
  );
}
